import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import * as Sentry from "@sentry/react";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import type { LocaleContextValue } from "./types";
import { DEFAULT_LOCALE } from "./types";
import { loadMessages } from "./loadMessages";
import { logIntlError, IntlErrorLike } from "./intlError";
import { useLazyLocaleMessages } from "./useLazyLocaleMessages";

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface AppIntlProviderProps {
  children: React.ReactNode;
  initialLocale?: LanguagesIsoEnum;
}

// App-root provider: owns the requested locale + setLocale; useLazyLocaleMessages does the load-then-swap.
export function AppIntlProvider({ children, initialLocale = DEFAULT_LOCALE }: AppIntlProviderProps) {
  const [targetLocale, setTargetLocale] = useState<LanguagesIsoEnum>(initialLocale);
  const { locale, messages } = useLazyLocaleMessages(targetLocale);
  // The locale most recently *asked* for, updated synchronously. targetLocale only catches up once a
  // bundle resolves, so it can't arbitrate concurrent calls: while a slow load is in flight it still
  // reads as the old locale, which would both no-op a request for that old value and let the slow one
  // land on top of a newer choice. This ref is what makes the last request win.
  const requestedLocaleRef = useRef<LanguagesIsoEnum>(initialLocale);

  // Await the bundle before flipping so callers can `await setLocale(...)` for readiness. A failed load
  // leaves targetLocale where it is and rejects: advancing it would strand the app on the en-US fallback
  // while reporting success to the caller, and would make every later retry a no-op, since the retry
  // arrives with `next === targetLocale`.
  const setLocale = useCallback(
    async (next: LanguagesIsoEnum) => {
      if (next === requestedLocaleRef.current) return;
      requestedLocaleRef.current = next;

      try {
        await loadMessages(next);
      } catch (error) {
        // Hand the ref back to what's actually applied, so a retry for this same locale isn't taken
        // for a no-op. Skip it if a newer request already owns the ref — that one decides what's next.
        if (requestedLocaleRef.current === next) requestedLocaleRef.current = targetLocale;
        Sentry.captureException(error, {
          tags: { component: "AppIntlProvider", action: "setLocale" },
          extra: { requestedLocale: next },
        });
        throw error;
      }

      // Superseded while this bundle loaded: drop it rather than overwriting the newer choice.
      if (requestedLocaleRef.current !== next) return;

      setTargetLocale(next);
    },
    [targetLocale],
  );

  const handleIntlError = useCallback(
    (err: IntlErrorLike) => logIntlError(err, { locale, component: "AppIntlProvider" }),
    [locale],
  );

  const localeContextValue = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={localeContextValue}>
      <ReactIntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages} onError={handleIntlError}>
        {children}
      </ReactIntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within an AppIntlProvider");
  }
  return context;
}
